import React from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CARD_BACK_DESIGNS, DEFAULT_CARD_BACK_DESIGN, getCardBackImage } from '../services/CardBackPreference';

export const DEFAULT_MONEY_CARDS = { upperShowCard: true, lowerShowCard: true, spadeAce: true,
    diamondSeven: true, redJoker: true, blackJoker: true, blueJoker: true };

export default function GameSettingsFields({ deckCount, jokerPairs, moneyCards, maxWaitSeconds, cardBackDesign = DEFAULT_CARD_BACK_DESIGN, onChange, readOnly = false }) {
    const { t } = useTranslation();
    const [previewDesignId, setPreviewDesignId] = React.useState(cardBackDesign);
    React.useEffect(() => setPreviewDesignId(cardBackDesign), [cardBackDesign]);
    const previewCardBack = getCardBackImage(previewDesignId);
    const values = { deckCount, jokerPairs, moneyCards, maxWaitSeconds, cardBackDesign };
    const change = patch => onChange({ ...values, ...patch });
    return <>
        <FormControl fullWidth margin="dense">
            <InputLabel id="game-deck-count">{t('gameHome.deckCount')}</InputLabel>
            <Select disabled={readOnly} labelId="game-deck-count" label={t('gameHome.deckCount')} value={deckCount}
                onChange={event => change({ deckCount: Number(event.target.value) })}>
                {[2, 3].map(count => <MenuItem key={count} value={count}>{t('gameHome.decks', { count })}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel id="game-joker-count">{t('gameHome.numberOfJokerPairs')}</InputLabel>
            <Select disabled={readOnly} labelId="game-joker-count" label={t('gameHome.numberOfJokerPairs')} value={jokerPairs}
                onChange={event => change({ jokerPairs: Number(event.target.value) })}>
                {[0, 1, 2, 3].map(pairs => <MenuItem key={pairs} value={pairs}>
                    {pairs === 0 ? t('gameHome.jokerless') : t(pairs === 1 ? 'gameSettings.oneJokerPair' : 'gameSettings.jokerPairs', { pairs, count: pairs * 2 })}
                </MenuItem>)}
            </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel shrink id="game-max-wait">{t('gameSettings.maxWait')}</InputLabel>
            <Select displayEmpty disabled={readOnly} labelId="game-max-wait" label={t('gameSettings.maxWait')} value={maxWaitSeconds ?? ''}
                renderValue={selected => selected === '' ? t('gameSettings.never') : t('gameSettings.seconds', { count: selected })}
                onChange={event => change({ maxWaitSeconds: event.target.value === '' ? null : Number(event.target.value) })}>
                <MenuItem value="">{t('gameSettings.never')}</MenuItem>
                {[20, 25, 30, 35, 40, 45, 50, 55, 60].map(count => <MenuItem key={count} value={count}>{t('gameSettings.seconds', { count })}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl component="fieldset" margin="normal" fullWidth sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, pb: 1 }}>
            <FormLabel component="legend" sx={{ px: 1 }}>{t('gameSettings.moneyCards')}</FormLabel>
            <FormGroup>
                {Object.keys(DEFAULT_MONEY_CARDS).map(key => <FormControlLabel key={key}
                    label={t(`gameSettings.${key}`)} control={<Checkbox disabled={readOnly} checked={Boolean(moneyCards[key])}
                        onChange={event => change({ moneyCards: { ...moneyCards, [key]: event.target.checked } })} />} />)}
            </FormGroup>
        </FormControl>
        <FormControl component="fieldset" margin="normal" fullWidth sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, pb: 2 }}>
            <FormLabel component="legend" sx={{ px: 1 }}>{t('gameSettings.chooseCardDesign')}</FormLabel>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t('gameSettings.cardDesignPersonal')}
            </Typography>
            <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220, mb: 1.5 }}>
                <Box component="img" src={previewCardBack} alt={t('gameSettings.cardDesignPreview')}
                    sx={{ display: 'block', width: 'auto', maxWidth: '100%', height: 210, objectFit: 'contain', borderRadius: 1, boxShadow: 4 }} />
            </Box>
            <Box className="card-design-picker" sx={{ display: 'flex', gap: 1, maxWidth: '100%', pb: 1,
                overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x proximity', WebkitOverflowScrolling: 'touch' }}>
                {CARD_BACK_DESIGNS.map((design, index) => <Box component="button" type="button" key={design.id}
                    aria-label={t('gameSettings.cardDesignOption', { count: index + 1 })} aria-pressed={design.id === previewDesignId}
                    disabled={readOnly} onClick={() => setPreviewDesignId(design.id)}
                    sx={{ p: 0.5, flex: '0 0 72px', minWidth: 72, scrollSnapAlign: 'start', border: '2px solid',
                        borderColor: design.id === previewDesignId ? 'primary.main' : 'divider', borderRadius: 1.5,
                        bgcolor: design.id === cardBackDesign ? 'action.selected' : 'transparent', cursor: readOnly ? 'default' : 'pointer' }}>
                    <Box component="img" src={design.image} alt="" sx={{ display: 'block', width: '100%', height: 92, objectFit: 'contain' }} />
                </Box>)}
            </Box>
            {!readOnly && <Button type="button" variant="contained" disabled={previewDesignId === cardBackDesign}
                onClick={() => change({ cardBackDesign: previewDesignId })} sx={{ alignSelf: 'center', mt: 1.5 }}>
                {previewDesignId === cardBackDesign ? t('gameSettings.selectedCardDesign') : t('gameSettings.useCardDesign')}
            </Button>}
        </FormControl>
    </>;
}
